// Full-screen overlays: the group panel and the Views an applet shows in the `overlay`
// target (client/lib/views.js; the launcher, applets/launcher) are each made with overlay()
// and share what this module owns: one open at a time, the page scrolling instead of a scroller of their own,
// closing on Escape or a click outside their content, and focus on open.
//
// An overlay is in the page's flow, not fixed over it: while one is open the webtop is pinned behind it
// (body.overlay-open, style.css), so the overlay alone makes the page's length, and one taller than the screen
// scrolls the page itself, as the webtop does, with no scrollbar of its own. The webtop's scroll position is held
// meanwhile (holdScroll() in geometry.js, so scrolled() still answers in the webtop's space) and given back on close.
import { paintChrome } from './background.js';
import { holdScroll, releaseScroll } from './geometry.js';
import { lastPointerType } from './input.js';
import { hideMenu, $menu } from './menu.js';
import { closePanel } from './notch-panel.js';

let current = null;

// `el`: the overlay's element. `keep`: a selector for its content; a click anywhere else in it closes it.
// `button`: the button that toggles it (pressed while it is open). `bodyClass`: also set on <body> while it is open
// (launcher-open trims the notch to the launcher button). `onOpen()` draws it and may return the element to focus
// (not after a tap, so no on-screen keyboard or focus ring); `onClose()` cleans up after it. `onEscape()` replaces
// closing on Escape (the group panel goes up a level).
export function overlay({
  el, keep, button = null, bodyClass = null, onOpen = () => null, onClose = () => {}, onEscape = null,
}) {
  const o = { el, keep, button, bodyClass, onOpen, onClose, onEscape };
  // A click whose press only dismissed a context menu leaves the overlay open (the press hides the menu before the
  // click lands).
  let menuWasOpen = false;
  el.addEventListener('pointerdown', () => { menuWasOpen = !$menu.hidden; }, true);
  el.addEventListener('click', (e) => { if (!menuWasOpen && !e.target.closest(o.keep)) closeOverlay(o); });
  return o;
}

export const isOpen = (o) => current === o;
// The open overlay's element id (launcher, webtops, widgets, group-panel; `overlay` for an applet's View
// that has none), or null: what the assistant is told is on screen, so "close" has something to close (@webtop/api's
// page.info(), applets/assistant; client/lib/actions.js).
export const openView = () => (current ? current.el.id || 'overlay' : null);
// The colour the open overlay paints across the screen, or null when the webtop's background shows behind it.
export function overlayColor() {
  if (!current) return null;
  const c = getComputedStyle(current.el).backgroundColor;
  return c === 'rgba(0, 0, 0, 0)' ? null : c;
}

export function openOverlay(o) {
  hideMenu();
  closePanel(); // the notch's panel would stay over it: a press in the notch does not close it
  if (current !== o) {
    if (current) shut(current); // one overlay at a time: the next one takes the held scroll over
    else {
      holdScroll();
      document.body.classList.add('overlay-open');
    }
    current = o;
    if (o.bodyClass) document.body.classList.add(o.bodyClass);
    o.button?.classList.add('active');
    o.button?.setAttribute('aria-expanded', 'true');
    o.el.hidden = false;
  }
  scrollTo(0, 0);
  const focus = o.onOpen();
  paintChrome(); // after onOpen: a launcher reopened with another spec may gain or lose its colour
  if (lastPointerType === 'touch') document.activeElement?.blur();
  else focus?.focus({ preventScroll: true });
}

export function closeOverlay(o = current) {
  if (!o || current !== o) return;
  shut(o);
  current = null;
  document.body.classList.remove('overlay-open');
  releaseScroll();
  paintChrome();
  clearAddress();
  // Nothing stays focused once it closes (a focused tile would keep looking pressed).
  document.activeElement?.blur();
}

// A new button or body class for an overlay, which takes effect at once if it is open (a launcher replacing another).
export function restyleOverlay(o, { button = null, bodyClass = null }) {
  const open = isOpen(o);
  if (open && o.button !== button) {
    o.button?.classList.remove('active');
    o.button?.setAttribute('aria-expanded', 'false');
    button?.classList.add('active');
    button?.setAttribute('aria-expanded', 'true');
  }
  if (open && o.bodyClass !== bodyClass) {
    if (o.bodyClass) document.body.classList.remove(o.bodyClass);
    if (bodyClass) document.body.classList.add(bodyClass);
  }
  o.button = button;
  o.bodyClass = bodyClass;
}

export const toggleOverlay = (o, open = !isOpen(o)) => (open ? openOverlay(o) : closeOverlay(o));

// An address says what to open (`?q=`, `#webtops`: the Launcher applet's fromAddress), so with nothing open any more
// it goes, wherever the overlay came from and however it closed, or a reload would open it again. No history entry
// and no hashchange.
export function clearAddress() {
  const url = new URL(location.href);
  url.searchParams.delete('q');
  url.hash = '';
  if (url.href !== location.href) history.replaceState(history.state, '', url);
}

function shut(o) {
  o.el.hidden = true;
  if (o.bodyClass) document.body.classList.remove(o.bodyClass);
  o.button?.classList.remove('active');
  o.button?.setAttribute('aria-expanded', 'false');
  o.onClose();
}

// A glyph drawn from icon markup (the client's own, never user text).
export function iconGlyph(markup) {
  const glyph = document.createElement('span');
  glyph.className = 'glyph';
  glyph.innerHTML = markup;
  return glyph;
}

// Escape closes the open overlay (or runs its onEscape). Capture phase, so it runs before the handler that hides the context menu: with a
// menu or a dialog open over it, Escape closes just that, and a second Escape closes the overlay.
export function init() {
  addEventListener('resize', () => { if (current) paintChrome(); }); // a rotated phone crosses the launcher's 640px
  addEventListener('keydown', (e) => {
    if (e.key !== 'Escape' || !current || !$menu.hidden || document.querySelector('dialog[open]')) return;
    if (current.onEscape) current.onEscape();
    else closeOverlay();
  }, true);
}
